import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSession } from '../context/Session';
import { useT } from '../i18n';
import { Empty, Icon, Loading, Modal, Spinner, initials, useConfirm, useToast } from '../components/ui';
import { useCollection, useCollectionOnce } from '../lib/hooks';
import { collection } from 'firebase/firestore';
import { db } from '../../firebase';
import { computeStreakSnapshot } from '../lib/streak';
import { DayDot } from './Home';
import { formatDate, tsToDate } from '../lib/dates';
import { deleteTrainerCode, generateTrainerCode, loadTrainees, normalizeUser, removeTraineeFromList, trainerCodesQuery, updateUser } from '../lib/services';
import { deleteField } from 'firebase/firestore';
import { useProgram } from '../lib/useUserData';
import { ProgramEditor } from './Program';
import { NutritionRecordsList } from './NutritionRecords';
import { ProgressView } from './Progress';

/**
 * Athlete's workout / nutrition streaks for the trainer (same calculation as the
 * athlete's Home card). Read once — see useCollectionOnce for why not live.
 */
function TraineeStreak({ trainee }) {
  const t = useT();
  const { program } = useProgram(trainee.exerciseProgramID);
  const progress = useCollectionOnce(collection(db, 'progress_workouts', trainee.id, 'days'), `tpw/${trainee.id}`);
  const nutrition = useCollectionOnce(collection(db, 'users', trainee.id, 'nutrition_days'), `tnd/${trainee.id}`);
  const snapshot = useMemo(
    () => computeStreakSnapshot({ progressDays: progress.docs, program, nutritionDays: nutrition.docs, userData: trainee }),
    [progress.docs, program, nutrition.docs, trainee],
  );
  if (progress.loading || nutrition.loading) return null;
  // Workout streak needs read access to progress_workouts (see firestore.rules).
  const workoutKnown = !progress.error;
  return (
    <div className="gm-row gm-wrap" style={{ marginTop: 10, gap: 14 }}>
      <span className="gm-strong" style={{ color: 'var(--gm-workout)', fontSize: 13 }} title={t('workoutStreakTitle')}>
        🔥 {workoutKnown ? snapshot.programStreakDays : '—'} {t('daysUnit')}
      </span>
      {!nutrition.error && (
        <span className="gm-row gm-strong" style={{ color: 'var(--gm-nutrition)', fontSize: 13, gap: 4 }} title={t('nutritionStreakTitle')}>
          <Icon name="restaurant_menu" size={15} /> {snapshot.nutritionStreakDays} {t('daysUnit')}
        </span>
      )}
      <span className="gm-grow" />
      <div className="gm-week-dots" style={{ marginTop: 0, gap: 6 }}>
        {snapshot.week.map((d) => (
          <div key={d.dayKey} className={`day${d.isToday ? ' today' : ''}`} title={t(d.dayKey)}>
            <DayDot color="var(--gm-workout)" done={workoutKnown && d.workoutDone} frozen={d.workoutFrozen} isToday={d.isToday} applicable={d.hasWorkout} />
            <DayDot color="var(--gm-nutrition)" done={d.nutritionLogged} frozen={d.nutritionFrozen} isToday={d.isToday} />
            <small>{t.dayShort(d.dayKey)}</small>
          </div>
        ))}
      </div>
    </div>
  );
}

function TraineeDetail({ trainee, view, onClose }) {
  const t = useT();
  const name = `${trainee.firstName || ''} ${trainee.lastName || ''}`.trim();
  const titles = {
    nutrition: `${name} – ${t('nutriAiTab')}`,
    progress: `${name} – ${t('progress')}`,
  };
  return (
    <Modal open wide onClose={onClose} title={titles[view]}>
      {view === 'nutrition' && <NutritionRecordsList uid={trainee.id} readOnly />}
      {view === 'progress' && <ProgressView uid={trainee.id} readOnly showRecords />}
    </Modal>
  );
}

function CodesCard({ user }) {
  const t = useT();
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [current, setCurrent] = useState(null);
  const { docs } = useCollection(trainerCodesQuery(user.email), user.email ? `codes/${user.email}` : null);
  const codes = useMemo(
    () => [...docs].sort((a, b) => (tsToDate(a.expiration) || 0) - (tsToDate(b.expiration) || 0)),
    [docs],
  );

  const generate = async () => {
    setBusy(true);
    try {
      const code = await generateTrainerCode(user.email, user.subscription);
      setCurrent(code);
    } catch (e) {
      toast.error(e.message === 'MAXIMUM_CODE_LIMIT_REACHED' ? t('maximumCodeLimitReached') : t('anErrorOccurred'));
    } finally {
      setBusy(false);
    }
  };

  const copy = async (code) => {
    try {
      await navigator.clipboard.writeText(code);
      toast.show(t('codeCopiedToClipboard'));
    } catch (e) {
      toast.show(code);
    }
  };

  const remove = async (code) => {
    try {
      await deleteTrainerCode(code);
      toast.show(t('codeDeleted'));
      if (current === code) setCurrent(null);
    } catch (e) {
      toast.error(t('anErrorOccurred'));
    }
  };

  return (
    <div className="gm-card gm-card--pad-lg">
      <div className="gm-row" style={{ alignItems: 'flex-start', marginBottom: 14 }}>
        <div className="gm-icon-tile"><Icon name="key" /></div>
        <div className="gm-grow">
          <div className="gm-card-title">{t('trainerCode')}</div>
          <div className="gm-muted gm-small" style={{ marginTop: 2 }}>{t('generateCodeForTrainees')}</div>
        </div>
      </div>
      {current && (
        <div className="gm-row" style={{ justifyContent: 'center', gap: 14, margin: '6px 0 16px' }}>
          <div style={{ fontSize: 38, fontWeight: 900, letterSpacing: 10 }}>{current}</div>
          <button type="button" className="gm-icon-btn" onClick={() => copy(current)} title={t('copyCode')}><Icon name="content_copy" outline /></button>
        </div>
      )}
      <button type="button" className="gm-btn gm-btn--block" onClick={generate} disabled={busy}>
        {busy ? <Spinner /> : <Icon name="add" />} {current ? t('generateNewCode') : t('generateCode')}
      </button>
      {codes.length > 0 && (
        <>
          <div className="gm-row-between" style={{ margin: '18px 0 6px' }}>
            <span className="gm-upper-sm">{t.upper(t('myCodes'))}</span>
            <span className="gm-pill">{t('codeCount', codes.length)}</span>
          </div>
          {codes.map((c) => {
            const exp = tsToDate(c.expiration);
            const expired = exp && exp < new Date();
            return (
              <div key={c.id} className="gm-list-item" style={{ padding: '10px 0' }}>
                <span style={{ fontWeight: 800, letterSpacing: 4, fontSize: 16, opacity: expired ? 0.45 : 1 }}>{c.code || c.id}</span>
                <span className="gm-grow gm-tiny gm-muted">
                  {expired ? t('expired') : t('expiresIn', formatDate(exp, t.locale, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }))}
                </span>
                {!expired && <button type="button" className="gm-icon-btn gm-icon-btn--sm" onClick={() => copy(c.code || c.id)} title={t('copyCode')}><Icon name="content_copy" outline /></button>}
                <button type="button" className="gm-icon-btn gm-icon-btn--sm" onClick={() => remove(c.id)} title={t('remove')}><Icon name="delete" outline /></button>
              </div>
            );
          })}
        </>
      )}
    </div>
  );
}

export default function Trainees() {
  const t = useT();
  const navigate = useNavigate();
  const toast = useToast();
  const confirm = useConfirm();
  const { user } = useSession();
  const ids = useMemo(() => (Array.isArray(user.traineeList) ? user.traineeList : []), [user.traineeList]);
  const idsKey = ids.join(',');
  const [trainees, setTrainees] = useState(null);
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    let alive = true;
    setTrainees(null);
    loadTrainees(ids).then((list) => alive && setTrainees(list));
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idsKey]);

  const disconnect = async (tr) => {
    const ok = await confirm({ title: t('disconnect'), message: t('disconnectTraineeConfirm'), confirmLabel: t('disconnect'), danger: true });
    if (!ok) return;
    try {
      await removeTraineeFromList(user.id, ids, tr.id);
      updateUser(tr.id, { trainerEmail: deleteField() }).catch(() => {});
      toast.show(t('traineeRemovedFromList'));
    } catch (e) {
      toast.error(t('failedToDisconnect'));
    }
  };

  return (
    <div className="gm-content gm-enter">
      <div className="gm-page-head"><h1 className="gm-page-title">{t.upper(t('myTrainees'))}</h1></div>
      <div className="gm-dash" style={{ gridTemplateColumns: 'minmax(0, 1.4fr) minmax(0, 1fr)' }}>
        <div>
          {trainees === null ? (
            <Loading />
          ) : trainees.length === 0 ? (
            <div className="gm-card gm-card--pad-lg">
              <Empty icon="groups">
                <div className="gm-strong" style={{ color: 'var(--gm-text)' }}>{t('noTraineesYet')}</div>
                <div>{t('shareTrainerCodeWithTrainees')}</div>
              </Empty>
            </div>
          ) : (
            <div className="gm-stack-sm">
              {trainees.map((tr) => {
                const name = `${tr.firstName || ''} ${tr.lastName || ''}`.trim() || tr.email || tr.id;
                const restricted = tr.restricted || tr.missing;
                return (
                  <div key={tr.id} className="gm-card">
                    <div className="gm-row">
                      <div className="gm-avatar" style={{ cursor: 'default' }}>{initials(tr)}</div>
                      <div className="gm-grow">
                        <div className="gm-strong">{restricted ? t('trainee') : name}</div>
                        <div className="gm-tiny gm-muted">
                          {restricted ? tr.id : [tr.email, tr.age ? t('ageValue', tr.age) : null].filter(Boolean).join(' · ')}
                        </div>
                      </div>
                      <button type="button" className="gm-icon-btn gm-icon-btn--sm" onClick={() => disconnect(tr)} title={t('disconnect')}><Icon name="person_remove" outline /></button>
                    </div>
                    {!restricted && <TraineeStreak trainee={tr} />}
                    <div className="gm-grid-3" style={{ marginTop: 12, gap: 8 }}>
                      <button type="button" className="gm-btn gm-btn--soft gm-btn--sm" disabled={restricted} onClick={() => navigate(`/trainees/${tr.id}/program`)}><Icon name="fitness_center" size={18} /> {t('programs')}</button>
                      <button type="button" className="gm-btn gm-btn--soft gm-btn--sm" onClick={() => setDetail({ trainee: tr, view: 'nutrition' })}><Icon name="restaurant" size={18} /> {t('nutriAiTab')}</button>
                      <button type="button" className="gm-btn gm-btn--soft gm-btn--sm" onClick={() => setDetail({ trainee: tr, view: 'progress' })}><Icon name="trending_up" size={18} /> {t('progress')}</button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        <CodesCard user={user} />
      </div>
      {detail && <TraineeDetail trainee={detail.trainee} view={detail.view} onClose={() => setDetail(null)} />}
    </div>
  );
}

/** Trainer editing an athlete's program — the same editor as the athlete's own Program page. */
export function TraineeProgramPage() {
  const t = useT();
  const navigate = useNavigate();
  const { traineeId } = useParams();
  const { user } = useSession();
  const [trainee, setTrainee] = useState(undefined);
  useEffect(() => {
    let alive = true;
    loadTrainees([traineeId]).then(([tr]) => alive && setTrainee(tr || null));
    return () => { alive = false; };
  }, [traineeId]);
  const programId = trainee && !trainee.restricted && !trainee.missing ? trainee.exerciseProgramID : null;
  const { program, loading, exists } = useProgram(programId);
  const isMine = Array.isArray(user.traineeList) && user.traineeList.includes(traineeId);
  const name = trainee ? `${trainee.firstName || ''} ${trainee.lastName || ''}`.trim() : '';

  const back = (
    <button type="button" className="gm-icon-btn" onClick={() => navigate('/trainees')} title={t('back')}><Icon name="arrow_back" /></button>
  );
  let body;
  if (trainee === undefined || (programId && loading)) body = <Loading />;
  else if (!isMine || !trainee || trainee.restricted || trainee.missing) body = <div className="gm-card"><Empty icon="lock">{t('errorLoadingData')}</Empty></div>;
  else if (!programId || !exists) body = <div className="gm-card"><Empty icon="event_busy">{t('noProgramAssigned')}</Empty></div>;
  else {
    return (
      <div className="gm-content gm-content--narrow gm-enter" style={{ paddingBottom: 160 }}>
        <div className="gm-row" style={{ marginBottom: 6 }}>
          {back}
          <span className="gm-muted gm-small">{t('myTrainees')}</span>
        </div>
        <ProgramEditor
          programId={programId}
          program={program}
          user={normalizeUser(trainee.id, trainee)}
          trainerMode
          title={t.upper(name ? t('traineeProgramName', name) : t('traineeProgram'))}
        />
      </div>
    );
  }
  return (
    <div className="gm-content gm-content--narrow gm-enter">
      <div className="gm-page-head">{back}<h1 className="gm-page-title">{t.upper(t('traineeProgram'))}</h1></div>
      {body}
    </div>
  );
}
